import React from "react";
import "./widgets.css";

/**
 * WidgetSuccessState - Reusable success/all-clear state
 */
export const WidgetSuccessState = ({ message, icon = "bi-check-circle" }) => {
  return (
    <div className="widget-success d-flex align-items-center text-success">
      <i className={`bi ${icon} me-2`}></i>
      <span className="small">{message}</span>
    </div>
  );
};

/**
 * WidgetMetricBox - Reusable metric display box
 */
export const WidgetMetricBox = ({
  label,
  value,
  color = "light",
  textColor = "dark",
}) => {
  return (
    <div className={`bg-${color} bg-opacity-10 rounded-2 p-2 text-center`}>
      <div className={`text-${textColor} small`}>{label}</div>
      <div className={`widget-metric-value text-${textColor}`}>{value}</div>
    </div>
  );
};

/**
 * WidgetProgressBar - Reusable progress bar
 */
export const WidgetProgressBar = ({
  segments = [],
  height = "4px",
  className = "",
}) => {
  return (
    <div className={`progress widget-progress ${className}`} style={{ height }}>
      {segments.map((segment, index) => (
        <div
          key={index}
          className={`progress-bar bg-${segment.color}`}
          style={{ width: `${segment.percent}%` }}
        />
      ))}
    </div>
  );
};

/**
 * WidgetHeader - Internal component for consistent widget headers
 */
const WidgetHeader = ({
  title,
  icon,
  iconColor,
  subtitle,
  badge,
  badgeColor,
  headerRight,
}) => {
  return (
    <div className="widget-header d-flex align-items-center justify-content-between mb-3">
      <div className="d-flex align-items-center">
        {icon && (
          <div
            className={`widget-icon widget-icon--${iconColor} rounded-circle p-2 me-2`}
          >
            <i className={`bi ${icon} text-${iconColor}`}></i>
          </div>
        )}
        <div>
          <h6 className="mb-0 fw-semibold">{title}</h6>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
      </div>
      <div className="d-flex align-items-center gap-2">
        {badge !== undefined && badge !== null && (
          <span className={`badge bg-${badgeColor}`}>{badge}</span>
        )}
        {headerRight}
      </div>
    </div>
  );
};

/**
 * WidgetCard - Standardized wrapper component for all dashboard widgets
 *
 * @param {string} title - Widget title
 * @param {string} subtitle - Optional subtitle text
 * @param {string} icon - Bootstrap icon class (e.g., "bi-speedometer2")
 * @param {string} iconColor - Color theme: "info", "primary", "purple", "orange", "danger", "warning", "success"
 * @param {string|number} badge - Optional badge content (e.g., count)
 * @param {string} badgeColor - Badge color class (default matches iconColor)
 * @param {boolean} loading - Show loading state
 * @param {boolean} error - Show error state
 * @param {string} errorMessage - Custom error message
 * @param {boolean} empty - Show empty state
 * @param {string} emptyMessage - Custom empty state message
 * @param {string} emptyIcon - Custom empty state icon (default: "bi-inbox")
 * @param {React.ReactNode} children - Widget content
 * @param {React.ReactNode} headerRight - Optional content for right side of header
 * @param {string} className - Additional classes for the card
 */
const WidgetCard = ({
  title,
  subtitle,
  icon,
  iconColor = "primary",
  badge,
  badgeColor,
  loading = false,
  error = false,
  errorMessage = "Unable to load data",
  empty = false,
  emptyMessage = "No data available",
  emptyIcon = "bi-inbox",
  children,
  headerRight,
  className = "",
}) => {
  const effectiveBadgeColor = badgeColor || iconColor;

  // Loading State
  if (loading) {
    return (
      <div className={`widget-card card shadow-sm border-0 ${className}`}>
        <div className="card-body p-3">
          <WidgetHeader
            title={title}
            icon={icon}
            iconColor={iconColor}
            subtitle={subtitle}
          />
          <div className="widget-loading text-center py-4">
            <div
              className={`spinner-border spinner-border-sm text-${iconColor}`}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="text-muted small mt-2">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className={`widget-card card shadow-sm border-0 ${className}`}>
        <div className="card-body p-3">
          <WidgetHeader
            title={title}
            icon={icon}
            iconColor={iconColor}
            subtitle={subtitle}
          />
          <div className="widget-error text-center py-4 text-muted">
            <i className="bi bi-cloud-slash fs-3 mb-2 d-block opacity-50"></i>
            <small>{errorMessage}</small>
          </div>
        </div>
      </div>
    );
  }

  // Empty State
  if (empty) {
    return (
      <div className={`widget-card card shadow-sm border-0 ${className}`}>
        <div className="card-body p-3">
          <WidgetHeader
            title={title}
            icon={icon}
            iconColor={iconColor}
            subtitle={subtitle}
          />
          <div className="widget-empty text-center py-4 text-muted">
            <i className={`bi ${emptyIcon} fs-3 mb-2 d-block opacity-50`}></i>
            <small>{emptyMessage}</small>
          </div>
        </div>
      </div>
    );
  }

  // Normal State with Content
  return (
    <div className={`widget-card card shadow-sm border-0 ${className}`}>
      <div className="card-body p-3">
        <WidgetHeader
          title={title}
          icon={icon}
          iconColor={iconColor}
          subtitle={subtitle}
          badge={badge}
          badgeColor={effectiveBadgeColor}
          headerRight={headerRight}
        />
        {children}
      </div>
    </div>
  );
};

export default WidgetCard;
